// CSR-2326 — AOR (Agent of Record change): the customer moved to another
// agency, so the carrier portal no longer lets our agent code open the policy.
// The policy stays in force; the Data Center owns the flag (CSR-2323).

// Who raised the flag — mirrors the DC `aor_source` enum.
export const AOR_SOURCES = ["carrier_scrape", "ams_manual"] as const;
export type AorSource = (typeof AOR_SOURCES)[number];

export function isAorSource(value: unknown): value is AorSource {
  return (AOR_SOURCES as readonly unknown[]).includes(value);
}

// The one reading of "flagged AOR'd away", shared by the client views and the
// server's COI aggregation so the two cannot drift.
export function isAorFlagged(policy: {
  aorIdentifiedAt?: string | null;
}): boolean {
  return !!policy.aorIdentifiedAt;
}

// CSR-2333 — a manual remark restating an excluded policy ("Motor Truck Cargo:
// Policy AOR-1, Limit $100,000") would print that coverage, because the live
// builder only de-dupes restatements of the rows it keeps. Mirrors logrock-coi.
export function aorPolicyNumbers(
  policies: ReadonlyArray<{
    aorIdentifiedAt?: string | null;
    policyNumber?: string | null;
  }>,
): string[] {
  return policies
    .filter(isAorFlagged)
    .map((p) => (p.policyNumber ?? "").trim())
    .filter(Boolean);
}

function lineNamesToken(line: string, token: string): boolean {
  const isWordChar = (ch: string | undefined) => !!ch && /[A-Za-z0-9]/.test(ch);
  for (let at = line.indexOf(token); at !== -1; at = line.indexOf(token, at + 1)) {
    if (!isWordChar(line[at - 1]) && !isWordChar(line[at + token.length])) {
      return true;
    }
  }
  return false;
}

// A line naming an AOR'd number as a whole token goes; every other line stays,
// in order, and a blank-line-separated segment the drop empties goes with it.
export function dropRemarkLinesNamingPolicies(
  text: string,
  policyNumbers: readonly string[],
): string {
  const tokens = policyNumbers.map((n) => n.trim()).filter(Boolean);
  if (tokens.length === 0) return text;
  return text
    .split("\n\n")
    .flatMap((segment) => {
      const lines = segment.split("\n");
      const kept = lines.filter(
        (line) => !tokens.some((token) => lineNamesToken(line, token)),
      );
      if (kept.length < lines.length && kept.every((line) => !line.trim())) {
        return [];
      }
      return [kept.join("\n")];
    })
    .join("\n\n");
}

// The activity-log rows an agent's flag and "Access restored" write. Exact
// {entityType, action} pairs, shared by the write (routes) and the policy feed
// read (`getActivityLogsForPolicy`) so the two cannot disagree — the same
// reasoning as ACORD_GENERATED_LOG.
export const AOR_FLAGGED_LOG = {
  entityType: "policy",
  action: "aor_flagged",
} as const;

export const AOR_RESTORED_LOG = {
  entityType: "policy",
  action: "aor_restored",
} as const;
